import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";

export const fetchWarehouseMaster = createAsyncThunk(
  "master/fetchWarehouseMaster",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Handle date-range filters (object with from_date / to_date)
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            if (
              from_date &&
              (filterField === "created_at" || filterField === "updated_at")
            ) {
              params.append(`${filterField}_from`, from_date);
            }

            if (
              to_date &&
              (filterField === "created_at" || filterField === "updated_at")
            ) {
              params.append(`${filterField}_to`, to_date);
            }
          }

          // 🔤 Handle simple (non-object) filters
          else {
            params.append(filterField, String(filterValue));
          }
        }
      );
    }

    const response: any = await Axios.get(
      `/master/warehouse?${params.toString()}`
    );

    return {
      data: response?.data?.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const exportWarehouseMasterCSV = async ({
  sort,
  order,
  search,
  filters,
}: {
  sort: any;
  order: any;
  search: any;
  filters: any;
}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }
  if (filters?.length) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (!filterField || filterValue === undefined || filterValue === null)
          return;

        // 🗓 Handle date-range filters (object with from_date / to_date)
        if (typeof filterValue === "object" && filterValue !== null) {
          const { from_date, to_date } = filterValue;

          if (
            from_date &&
            (filterField === "created_at" || filterField === "updated_at")
          ) {
            params.append(`${filterField}_from`, from_date);
          }

          if (
            to_date &&
            (filterField === "created_at" || filterField === "updated_at")
          ) {
            params.append(`${filterField}_to`, to_date);
          }
        }

        // 🔤 Handle simple (non-object) filters
        else {
          params.append(filterField, String(filterValue));
        }
      }
    );
  }
  try {
    const response = await Axios.get(`/master/warehouse?${params.toString()}`, {
      params: { export: "csv" },
      responseType: "blob", // important
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "warehouses.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading CSV", error);
  }
};

// Configure Warehouse Thunks
export const fetchConfigureWarehouseList = createAsyncThunk(
  "master/fetchConfigureWarehouseList",
  async (warehouseId?: string) => {
    try {
      // TODO: Replace with actual API endpoint
      const url = warehouseId 
        ? `/master/warehouse/configure/list/${warehouseId}/`
        : `/master/warehouse/configure/list/`;
      const response: any = await Axios.get(url);
      return {
        data: response?.data?.data || [],
      };
    } catch (error: any) {
      console.error("Error fetching configure warehouse list:", error);
      throw new Error(error?.response?.data?.message || "Failed to fetch configurations");
    }
  }
);

export const addConfigureWarehouse = createAsyncThunk(
  "master/addConfigureWarehouse",
  async (payload: {
    warehouseId: string;
    organization: string;
    department: string;
    team: string;
    user: string;
  }) => {
    try {
      // TODO: Replace with actual API endpoint
      const response: any = await Axios.post(
        `/master/warehouse/configure/create/${payload.warehouseId}/`,
        {
          organization: payload.organization,
          department: payload.department,
          team: payload.team,
          user: payload.user,
        }
      );
      return response?.data?.data || {};
    } catch (error: any) {
      console.error("Error adding configure warehouse:", error);
      throw new Error(error?.response?.data?.message || "Failed to add configuration");
    }
  }
);

export const deleteConfigureWarehouse = createAsyncThunk(
  "master/deleteConfigureWarehouse",
  async (id: string) => {
    try {
      // TODO: Replace with actual API endpoint
      const response: any = await Axios.delete(
        `/master/warehouse/configure/delete/${id}/`
      );
      return { id, data: response?.data?.data || {} };
    } catch (error: any) {
      console.error("Error deleting configure warehouse:", error);
      throw new Error(error?.response?.data?.message || "Failed to delete configuration");
    }
  }
);

export const updateConfigureWarehouse = createAsyncThunk(
  "master/updateConfigureWarehouse",
  async (payload: { rows: any[] }) => {
    try {
      // TODO: Replace with actual API endpoint
      const response: any = await Axios.put(
        `/master/warehouse/configure/update/`,
        payload
      );
      return response?.data?.data || {};
    } catch (error: any) {
      console.error("Error updating configure warehouse:", error);
      throw new Error(error?.response?.data?.message || "Failed to update configurations");
    }
  }
);

export const fetchWarehouseDropdown = createAsyncThunk(
  "master/fetchWarehouseDropdown",
  async () => {
    try {
      const response: any = await Axios.get(`/master/warehouse/dropdown/`);
      return response?.data || [];
    } catch (error: any) {  
      console.error("Error fetching warehouse dropdown:", error);
      throw new Error(error?.response?.data?.message || "Failed to fetch warehouse dropdown");
    }
  }
);

export const fetchWarehouseDropdownAll = createAsyncThunk(
  "master/fetchWarehouseDropdownAll",
  async () => {
    try {
      const response: any = await Axios.get(`/master/warehouse/dropdown_all/`);
      return response?.data || [];
    } catch (error: any) {  
      console.error("Error fetching warehouse dropdown all:", error);
      throw new Error(error?.response?.data?.message || "Failed to fetch warehouse dropdown all");
    }
  }
);
